import { EventTrackLabelType } from '../constants/ga4';
import { CanceledTransactionEvent, LoginEvent, PromotionEvent, TrackingItem } from '../types/ga4';
import { logger } from '../utils/logger';

interface Ticket {
    id: string;
    name: string;
    selected: number;
    price: number;
    sessionName: string;
    group: {
      name: string;
    };
    couponName?: string;
    discountValue?: number;
    priceWithDiscount?: number;
  }

  interface Transaction {
    id: string;
    affiliated: string;
    total: number;
    totalTax: number;
    tickets: Ticket[];
    eventTitle: string;
  }

  interface CartItem {
    item_id: string;
    item_name: string;
    item_category: string;
    item_brand: string;
    item_variant: string;
    quantity: number;
    price: number;
    discount?: number;
    coupon?: string;
  }

class GoogleAnalytics {
  private currency = 'BRL';
  private totalPrice = 0;
  private coupon: string | null = null;
  private items: Array<CartItem> = [];
  private debug = false;

  init(analyticId: string): void {
    if (!analyticId) {
      logger.error('Google Analytics ID is required for initialization');
      return;
    }

    this._trigger('config', analyticId, {
      country : 'BR',
      currency : this.currency,
    });

    logger.info(`Google Analytics initialized with ID: ${analyticId}`);
  }

  private _trigger(action: string, name: string, params?: object): boolean {
    if (!window.gtag) {
      logger.error('Google Analytics is not available');
      return false;
    }

    window.gtag(action, name, params);
    return true;
  }

  trackPageView(location: string): void {
    if (location) {
      this._trigger('event', 'page_view', {
        page_path : location,
      });
      logger.info(`Page view tracked: ${location}`);
    }
  }

  trackTransaction(transaction: Transaction, coupon?: string): void {
    const params = {
      transaction_id : transaction.id,
      affiliation : transaction.affiliated,
      value : transaction.total,
      tax : transaction.totalTax,
      currency : this.currency,
      shipping : 0,
    };

    this._trigger('event', 'purchase', coupon ? { ...params, coupon } : params);
    this.addTransactionItems(transaction);
  }

  trackPromotionView(
    eventTrackLabel : EventTrackLabelType,
    { eventId, eventTitle, couponInput }: PromotionEvent,
  ) {
    this._trigger('event', eventTrackLabel, {
      promotion_name : 'Embedstore Coupon',
      items : [{
        item_id : eventId,
        item_name : eventTitle,
        coupon : couponInput,
      }],
    });
  }

  trackTransactionCancelled(
    eventTrackLabel : EventTrackLabelType,
    tackingData: CanceledTransactionEvent,
  ) {
    this._trigger('event', eventTrackLabel, tackingData);
  }

  trackLoginEvent(
    eventTrackLabel : EventTrackLabelType, { method }: LoginEvent
  ) {
    this._trigger('event', eventTrackLabel, { method });
  }

  trackViewItem(eventTrackLabel : EventTrackLabelType, trackingData: TrackingItem[]) {
    this._trigger('event', eventTrackLabel, { items : trackingData } );
  }

  private addTransactionItems(transaction: Transaction): void {
    transaction.tickets.forEach((ticket) => {
      const item: CartItem = {
        item_id : ticket.id,
        item_name : transaction.eventTitle,
        item_category : 'Ingressos',
        item_brand : ticket.group.name,
        item_variant : ticket.name,
        quantity : ticket.selected,
        price : ticket.price,
        coupon : ticket.couponName,
        discount : ticket.discountValue,
      };

      this.items.push(item);
    });

    logger.info(`Transaction items added: ${this.items.length}`);
  }

  addToCart(event: { data: { id: string; title: string } }, tickets: Ticket[], totalPrice: number): void {
    const items = tickets.map((ticket) => ({
      item_id : event.data.id,
      item_name : event.data.title,
      item_category : ticket.sessionName,
      item_brand : ticket.group.name,
      item_variant : ticket.name,
      quantity : ticket.selected,
      price : ticket.priceWithDiscount || ticket.price,
      discount : ticket.discountValue,
      coupon : ticket.couponName || '',
    }));

    this.items = items;
    this.totalPrice = totalPrice;
    this.coupon = items[0]?.coupon || '';

    this.trackEvent('add_to_cart', {
      currency : this.currency,
      value : totalPrice,
      items,
    });

    logger.info(`Cart state: ${this.items.length} items added`);
  }

  trackEvent(name: string, content: object): void {
    this._trigger('event', name, content);
    if (this.debug) {
      logger.info(`GA trackEvent: ${name}`);
    }
  }
}

export default new GoogleAnalytics();
